import React, {useEffect, useState} from "react";
import ComparisonChart from "../../charts/comparison-chart";
import DateRangePicker from "../../date-range-picker/date-range-picker";
import {instanceMob} from "../../../utils/utils";
import useLocationStore from "../config/use-location-store";
import Dropdown from "../../dropdown/dropdown";

const IncomeExpenseChartPanel = () => {
    const [selectedType, setSelectedType] = useState("income");
    const [selectedSaleType, setSelectedSaleType] = useState("");
    const [activePeriod, setActivePeriod] = useState("period1");
    const [showPicker, setShowPicker] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [periods, setPeriods] = useState({
        period1: "Periodo 1",
        period2: "Periodo 2",
    });
    const [activePeriods, setActivePeriods] = useState(["period1", "period2"]);
    const {selectedLocation} = useLocationStore();

    const defaultData = [
        {name: "Enero", period1: 4000, period2: 2400},
        {name: "Febrero", period1: 3000, period2: 1398},
        {name: "Marzo", period1: 2000, period2: 9800},
        {name: "Abril", period1: 2780, period2: 3908},
        {name: "Mayo", period1: 1890, period2: 4800},
        {name: "Junio", period1: 2390, period2: 3800},
        {name: "Julio", period1: 3490, period2: 4300},
    ];

    const buildQueryParams = () => {
        const params = new URLSearchParams();
        params.append("type", selectedType);
        params.append("location_id", selectedLocation.id);

        if (selectedSaleType) params.append("sale_type", selectedSaleType);

        const isValidRange = (str) =>
            /^\d{2}-\d{2}-\d{4}\s–\s\d{2}-\d{2}-\d{4}$/.test(str);

        if (isValidRange(periods.period1)) {
            const [start1, end1] = periods.period1.split(" – ");
            params.append("range1", `${start1}=${end1}`);
        }

        if (isValidRange(periods.period2)) {
            const [start2, end2] = periods.period2.split(" – ");
            params.append("range2", `${start2}=${end2}`);
        }

        return params.toString();
    };

    const handleApplyPeriod = (range) => {
        if (activePeriod) {
            if (range?.start && range?.end) {
                setPeriods((prev) => ({
                    ...prev,
                    [activePeriod]: `${range.start} – ${range.end}`,
                }));
            } else {
                setPeriods((prev) => ({
                    ...prev,
                    [activePeriod]:
                        activePeriod === "period1" ? "Periodo 1" : "Periodo 2",
                }));
            }
        }

        setShowPicker(false);
        setActivePeriod(null);
    };

    const fetchChartData = () => {
        const query = buildQueryParams();
        instanceMob
            .get(`admin/accounting/annual-summary?${query}`)
            .then((res) => {
                const response = res.data;

                const period1 = response.period_1?.data ?? [];
                const period2 = response.period_2?.data ?? [];

                const noData = period1.length === 0 && period2.length === 0;

                const defaultMonths = [
                    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
                    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"
                ];

                const allMonths = noData
                    ? defaultMonths
                    : [
                        ...new Set([
                            ...period1.map((d) => d.month),
                            ...period2.map((d) => d.month),
                        ]),
                    ];

                const mergedData = allMonths.map((month) => ({
                    name: month,
                    period1: period1.find((d) => d.month === month)?.total || 0,
                    period2: period2.find((d) => d.month === month)?.total || 0,
                }));
                setChartData(mergedData);
            })
            .catch((err) => {
                console.error("Error fetching chart data:", err);

                const defaultMonths = [
                    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
                    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"
                ];
                const mockData = defaultMonths.map((month) => ({
                    name: month,
                    period1: 0,
                    period2: 0,
                }));
                setChartData(mockData);
                setActivePeriods(["period1", "period2"]);
            });
    };

    useEffect(() => {
        fetchChartData();
    }, [selectedType, selectedSaleType, periods.period1, periods.period2, selectedLocation]);

    const isMock = chartData === defaultData;
    const chartColors = isMock
        ? ["#9ca3af", "#cbd5e1"]
        : ["#6FB75D", "#7086FD"];

    return (
        <>
            <div className="accounting-dashboard-income-expense-chart-container d-flex flex-column gap-3 p-4">
                <div className="d-flex flex-row justify-content-between">
                    <div className="accounting-dashboard-income-expense-chart-title">
                        Comparación de Ingresos y Egresos Anuales
                    </div>
                    <Dropdown
                        placeholder="Selecciona un tipo de venta"
                        width={220}
                        items={[
                            {id: "", name: "Ventas general"},
                            {id: "plan", name: "Ventas de planes gimnasio"},
                            {id: "product", name: "Ventas de productos tienda"},
                        ]}
                        onSelect={(item) => setSelectedSaleType(item.id)}
                    />
                </div>

                <div className="accounting-dashboard-income-expense-chart-filters d-flex flex-row gap-2">
                    <button
                        className="btn accounting-dashboard-income-expense-chart-button-filter"
                        onClick={() => setSelectedType("income")}
                    >
                        <input
                            className="form-check-input"
                            type="radio"
                            checked={selectedType === "income"}
                            readOnly
                        />
                        Ingresos
                    </button>
                    <button
                        className="btn accounting-dashboard-income-expense-chart-button-filter"
                        onClick={() => setSelectedType("outcome")}
                    >
                        <input
                            className="form-check-input"
                            type="radio"
                            checked={selectedType === "outcome"}
                            readOnly
                        />
                        Egresos
                    </button>

                    <div className="accounting-dashboard-income-expense-chart-divider"></div>

                    <div className="d-flex flex-row gap-3">
                        <div className="d-flex flex-row gap-1 align-items-center">
                            <button
                                className="btn accounting-dashboard-income-expense-chart-button-filter"
                                onClick={() => {
                                    setActivePeriod("period1");
                                    setShowPicker(true);
                                }}
                            >
                                {periods.period1}
                            </button>
                            <div className="accounting-dashboard-income-expense-chart-dot green"/>
                        </div>

                        <div className="d-flex flex-row gap-1 align-items-center">
                            <button
                                className="btn accounting-dashboard-income-expense-chart-button-filter"
                                onClick={() => {
                                    setActivePeriod("period2");
                                    setShowPicker(true);
                                }}
                            >
                                {periods.period2}
                            </button>
                            <div className="accounting-dashboard-income-expense-chart-dot purple"/>
                        </div>
                    </div>
                </div>

                <div className="accounting-dashboard-income-expense-chart">
                    <ComparisonChart
                        data={chartData}
                        dataKeys={activePeriods}
                        colors={chartColors}
                    />
                </div>
            </div>

            <DateRangePicker
                show={showPicker}
                handleClose={() => setShowPicker(false)}
                onApply={handleApplyPeriod}
                currentRange={
                    activePeriod ? periods[activePeriod] : null
                }
            />
        </>
    );
};

export default IncomeExpenseChartPanel;
